import React, { useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * "Keep my target" — the operator's reply to a verdict, and the muted chip it leaves behind.
 *
 * WHY IT EXISTS. A verdict is a readout, not an event: it re-derives every computation cycle, so
 * an unchanged situation republishes the same amber block indefinitely. An operator who has read
 * the argument and kept their target on purpose would be told the same thing forever — which
 * trains blindness, and alert fatigue does not degrade the noisy alert, it degrades every alert
 * in the channel.
 *
 * TONE. The ack never argues back and never implies the operator was wrong — it records that the
 * case was made and they chose. So it is deliberately the QUIETEST thing in the block: bare text
 * against the solid recommendation button and the bordered guardrails link. Taking the advice
 * stays the visually obvious option; keeping your target is available, not advertised. And the
 * chip that remains is slate, never emerald — this is a state of the conversation, not an
 * achievement.
 *
 * Both surfaces (dashboard Target Outlook, Targets page rows) render THESE components rather than
 * their own copies, for the same reason the backend owns the verdict's wording: two spellings of
 * one action is how a product ends up meaning two different things by it.
 *
 * The caller supplies `onDone` and does its own prop reload — this file stays free of the Inertia
 * router so it can be rendered (and tested) anywhere.
 */

interface AckRequest {
  /** The property's backend-served canonical path. The endpoint is this + a fixed suffix. */
  propertyBasePath: string
  month: number
}

function ackUrl({ propertyBasePath, month }: AckRequest): string {
  return `${propertyBasePath}/targets/${month}/verdict-ack`
}

/** POST/DELETE with the pending + error state both affordances need. */
function useAckRequest(request: AckRequest, method: 'POST' | 'DELETE', onDone?: () => void) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    if (pending) return
    setPending(true)
    setError(null)

    try {
      const response = await apiFetch(ackUrl(request), { method })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setError(data.error || 'We could not save that. Please try again.')
        return
      }
      onDone?.()
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setPending(false)
    }
  }

  return { send, pending, error }
}

interface KeepTargetButtonProps {
  propertyBasePath: string
  month: number
  monthName: string
  /** Matches the block it sits in: amber for `attention`, muted for `watch`. */
  tone?: 'amber' | 'muted'
  onDone?: () => void
  className?: string
}

export function KeepTargetButton({
  propertyBasePath,
  month,
  monthName,
  tone = 'amber',
  onDone,
  className,
}: KeepTargetButtonProps) {
  const { send, pending, error } = useAckRequest({ propertyBasePath, month }, 'POST', onDone)

  return (
    <span className={cn('inline-flex flex-col items-start gap-1', className)}>
      <button
        type="button"
        onClick={send}
        disabled={pending}
        title={`Stop showing this for ${monthName}. It comes back if the target or the situation changes.`}
        className={cn(
          'inline-flex items-center rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-60',
          tone === 'amber'
            ? 'text-amber-800/70 hover:bg-amber-100/80 hover:text-amber-900'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        {pending ? 'Saving…' : 'Keep my target'}
      </button>
      {error && <span className="text-[11px] leading-snug text-rose-600">{error}</span>}
    </span>
  )
}

interface AcknowledgedChipProps {
  propertyBasePath: string
  month: number
  monthName: string
  onDone?: () => void
  className?: string
}

/**
 * What an acknowledged month wears once its block is gone. It has to be findable — an operator
 * who silenced something in March needs to see, in September, that they did — and it has to carry
 * its own undo, because the block that would otherwise offer one is exactly what it removed.
 */
export function AcknowledgedChip({ propertyBasePath, month, monthName, onDone, className }: AcknowledgedChipProps) {
  const { send, pending, error } = useAckRequest({ propertyBasePath, month }, 'DELETE', onDone)

  return (
    <span className={cn('inline-flex flex-col items-start gap-0.5', className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-default items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2 pr-1 text-[10px] font-semibold text-slate-600">
              Acknowledged
              <button
                type="button"
                onClick={send}
                disabled={pending}
                aria-label={`Show the ${monthName} verdict again`}
                className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-50"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px]">
            You kept this target after reading the engine’s case, so it has stopped raising it. It comes back if the
            target or the situation changes.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {error && <span className="text-[11px] leading-snug text-rose-600">{error}</span>}
    </span>
  )
}
